import { useState } from 'react'
import { Link } from 'react-router-dom'
import api, { contentAPI, contentCalendarAPI } from '../services/api'
import type { Content as ContentType, Publication } from '../types'
import toast from 'react-hot-toast'
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import {
  SparklesIcon,
  RocketLaunchIcon,
  ClockIcon,
  CalendarIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline'
import Spinner from '../components/Spinner'
import { apiErrorMessage } from '../utils/apiError'
import { publishFailureText } from '../utils/publishFailure'
import { STAND_IN_MESSAGE } from '../utils/draftFlags'
import { useAuthStore } from '../store/authStore'

const renderMarkdown = (text: string) =>
  DOMPurify.sanitize(marked.parse(text, { async: false }))

// Format a Date as a "YYYY-MM-DDTHH:mm" string for use as the value/min of a
// <input type="datetime-local"> in the user's local timezone.
const toDatetimeLocalString = (date: Date) => {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

const CreateContent = () => {
  // Publishing posts to FinCreator's shared house accounts, so it's admin only.
  const isAdmin = useAuthStore((state) => !!state.user?.is_superuser)
  const [step, setStep] = useState<1 | 2 | 3>(1)
  const [generating, setGenerating] = useState(false)
  const [generatedContent, setGeneratedContent] = useState<ContentType[]>([])
  const [scheduledAt, setScheduledAt] = useState('')
  const [scheduling, setScheduling] = useState(false)

  // Analysis Form State
  const [analysis, setAnalysis] = useState({
    symbol: '',
    direction: 'LONG',
    entry: '',
    target: '',
    stop_loss: '',
    reasoning: '',
    timeframe: '1H'
  })

  // Platform Selection
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([
    'twitter',
    'instagram',
    'youtube',
    'tiktok',
    'blog'
  ])

  const platforms = [
    { id: 'twitter', name: 'Twitter', icon: '𝕏', description: '280 chars + hashtags' },
    { id: 'instagram', name: 'Instagram', icon: '📸', description: 'Visual caption + 15 hashtags' },
    { id: 'youtube', name: 'YouTube', icon: '▶️', description: 'Full script + timestamps' },
    { id: 'tiktok', name: 'TikTok', icon: '🎵', description: 'Hook-content-CTA format' },
    { id: 'blog', name: 'Blog', icon: '📝', description: '1500-word article' }
  ]

  const handlePlatformToggle = (platformId: string) => {
    setSelectedPlatforms(prev =>
      prev.includes(platformId)
        ? prev.filter(p => p !== platformId)
        : [...prev, platformId]
    )
  }

  const handleGenerateAll = async () => {
    // Validation
    if (!analysis.symbol || !analysis.entry || !analysis.target || !analysis.stop_loss) {
      toast.error('Please fill in all required fields')
      return
    }

    if (selectedPlatforms.length === 0) {
      toast.error('Please select at least one platform')
      return
    }

    setGenerating(true)
    setStep(2)

    try {
      // Call the bulk generation endpoint using the configured axios instance
      const response = await api.post('/api/content/generate-all', {
        user_analysis: {
          symbol: analysis.symbol,
          direction: analysis.direction,
          entry: parseFloat(analysis.entry),
          target: parseFloat(analysis.target),
          stop_loss: parseFloat(analysis.stop_loss),
          reasoning: analysis.reasoning,
          timeframe: analysis.timeframe
        },
        platforms: selectedPlatforms
      })

      const contents = response.data
      setGeneratedContent(contents)
      setStep(3)
      toast.success(`Generated content for ${contents.length} platforms!`)
    } catch (error) {
      console.error('Generation error:', error)
      toast.error(apiErrorMessage(error, 'Failed to generate content'))
      setStep(1)
    } finally {
      setGenerating(false)
    }
  }

  const resetForm = () => {
    setStep(1)
    setAnalysis({
      symbol: '',
      direction: 'LONG',
      entry: '',
      target: '',
      stop_loss: '',
      reasoning: '',
      timeframe: '1H'
    })
    setGeneratedContent([])
    setScheduledAt('')
  }

  // Each post goes to FinCreator's account for its own network. Blog posts
  // are draft-only and are never published.
  const handlePublishAll = async () => {
    try {
      const publishPromises = generatedContent
        .filter((content) => content.content_type !== 'blog')
        .map((content) => contentAPI.publish(content.id))
      const results: Publication[][] = await Promise.all(publishPromises)
      const publications = results.flat()

      if (publications.length === 0) {
        toast.error(
          'No platforms are configured for publishing. Add an active platform with auto-publish enabled.'
        )
        return
      }

      const successCount = publications.filter((p) => p.is_published).length
      if (successCount === publications.length) {
        toast.success("Published to FinCreator's accounts!")
      } else if (successCount > 0) {
        toast.success(`Published to ${successCount} of ${publications.length} platform(s)`)
      } else {
        toast.error(publishFailureText(publications, 'Failed to publish to any platform'))
        return
      }

      resetForm()
    } catch (error) {
      toast.error('Some publications failed')
    }
  }

  const handleScheduleAll = async () => {
    if (!scheduledAt) {
      toast.error('Please choose a date and time to schedule')
      return
    }

    const scheduledDate = new Date(scheduledAt)
    if (scheduledDate.getTime() <= Date.now()) {
      toast.error('Scheduled time must be in the future')
      return
    }

    // Each draft is scheduled as itself, so it goes to its own network (an
    // Instagram draft to Instagram). Blog drafts are never published.
    const publishable = generatedContent.filter((content) => content.content_type !== 'blog')
    if (publishable.length === 0) {
      toast.error('Blog drafts are draft-only, so there is nothing to schedule.')
      return
    }

    setScheduling(true)
    try {
      await Promise.all(
        publishable.map((content) =>
          contentCalendarAPI.schedule({
            content_type: 'POST',
            content_id: content.id,
            title: content.title || `${content.content_type} post`,
            body: content.text,
            scheduled_at: scheduledDate.toISOString(),
            tags: content.hashtags || [],
          })
        )
      )

      toast.success(
        `Scheduled ${publishable.length} post${publishable.length !== 1 ? 's' : ''} for ${scheduledDate.toLocaleString()}` +
          (publishable.length < generatedContent.length ? ' (blog drafts stay drafts)' : '')
      )
      resetForm()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to schedule content')
    } finally {
      setScheduling(false)
    }
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="text-center space-y-2">
        <h2 className="text-4xl font-bold text-gray-900">Create Content</h2>
        <p className="text-lg text-gray-600">
          Turn 1 trading idea into {platforms.length} platform posts in under 60 seconds
        </p>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center justify-center gap-4">
        <StepIndicator number={1} label="Import Analysis" active={step === 1} completed={step > 1} />
        <div className="w-24 h-0.5 bg-gray-300" />
        <StepIndicator number={2} label="Generate Content" active={step === 2} completed={step > 2} />
        <div className="w-24 h-0.5 bg-gray-300" />
        <StepIndicator number={3} label="Publish" active={step === 3} completed={false} />
      </div>

      {/* Step 1: Import Analysis */}
      {step === 1 && (
        <div className="space-y-6">
          {/* Analysis Form */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center gap-2 mb-4">
              <SparklesIcon className="h-6 w-6 text-primary-600" />
              <h2 className="text-xl font-bold text-gray-900">Your Trading Analysis</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Symbol <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={analysis.symbol}
                  onChange={(e) => setAnalysis({ ...analysis, symbol: e.target.value })}
                  placeholder="e.g., BTC/USDT"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Direction <span className="text-red-500">*</span>
                </label>
                <select
                  value={analysis.direction}
                  onChange={(e) => setAnalysis({ ...analysis, direction: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="LONG">LONG (Buy)</option>
                  <option value="SHORT">SHORT (Sell)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Entry Price <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={analysis.entry}
                  onChange={(e) => setAnalysis({ ...analysis, entry: e.target.value })}
                  placeholder="45000"
                  step="0.01"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Target Price <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={analysis.target}
                  onChange={(e) => setAnalysis({ ...analysis, target: e.target.value })}
                  placeholder="48000"
                  step="0.01"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Stop Loss <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={analysis.stop_loss}
                  onChange={(e) => setAnalysis({ ...analysis, stop_loss: e.target.value })}
                  placeholder="43500"
                  step="0.01"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Timeframe
                </label>
                <select
                  value={analysis.timeframe}
                  onChange={(e) => setAnalysis({ ...analysis, timeframe: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="5m">5 minutes</option>
                  <option value="15m">15 minutes</option>
                  <option value="1H">1 hour</option>
                  <option value="4H">4 hours</option>
                  <option value="1D">1 day</option>
                  <option value="1W">1 week</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reasoning / Analysis
                </label>
                <textarea
                  value={analysis.reasoning}
                  onChange={(e) => setAnalysis({ ...analysis, reasoning: e.target.value })}
                  placeholder="e.g., Bullish engulfing on 4H, RSI oversold, volume spike..."
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
          </div>

          {/* Platform Selection */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Select Platforms</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {platforms.map((platform) => (
                <button
                  key={platform.id}
                  onClick={() => handlePlatformToggle(platform.id)}
                  className={`p-4 border-2 rounded-lg text-left transition-all ${
                    selectedPlatforms.includes(platform.id)
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-2xl">{platform.icon}</span>
                    {selectedPlatforms.includes(platform.id) && (
                      <CheckCircleIcon className="h-5 w-5 text-primary-600" />
                    )}
                  </div>
                  <div className="font-semibold text-gray-900">{platform.name}</div>
                  <div className="text-xs text-gray-500 mt-1">{platform.description}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Generate Button */}
          <div className="flex justify-center">
            <button
              onClick={handleGenerateAll}
              disabled={generating}
              className="flex items-center gap-2 px-8 py-4 bg-primary-600 text-white text-lg font-semibold rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-all"
            >
              <RocketLaunchIcon className="h-6 w-6" />
              Generate Content for {selectedPlatforms.length} Platform{selectedPlatforms.length !== 1 ? 's' : ''}
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Generating */}
      {step === 2 && (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <div className="flex justify-center mb-6">
            <Spinner size="lg" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Generating Content</h2>
          <p className="text-gray-600 mb-4">
            Creating platform-optimized posts for {selectedPlatforms.length} channels
          </p>
          <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
            <ClockIcon className="h-5 w-5" />
            <span>This usually takes 30-45 seconds</span>
          </div>
        </div>
      )}

      {/* Step 3: Review & Publish */}
      {step === 3 && (
        <div className="space-y-6">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <CheckCircleIcon className="h-6 w-6 text-green-600" />
              <div>
                <div className="font-semibold text-green-900">Content Generated Successfully!</div>
                <div className="text-sm text-green-700">
                  Generated {generatedContent.length} platform-optimized posts
                </div>
              </div>
              {/* Each post is saved as a draft; Library opens them. */}
              <Link
                to="/content"
                state={{ openIds: generatedContent.map((c) => c.id) }}
                className="ml-auto text-sm font-medium text-green-800 underline hover:text-green-900"
              >
                View drafts in Library
              </Link>
            </div>
          </div>

          {/* Generated Content Preview */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {generatedContent.map((content) => (
              <div key={content.id} className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs font-medium text-gray-500 uppercase">
                    {content.content_type}
                  </span>
                  {content.stand_in ? (
                    <span className="px-2 py-1 text-xs font-medium rounded bg-amber-50 text-amber-800">
                      Stand-in text
                    </span>
                  ) : (
                    <span className="px-2 py-1 text-xs font-medium rounded bg-success-50 text-success-700">
                      Ready to publish
                    </span>
                  )}
                </div>

                {content.stand_in && (
                  <p role="note" className="mb-3 text-xs text-amber-800" data-testid="flag-stand-in">
                    {STAND_IN_MESSAGE}
                  </p>
                )}

                {content.title && (
                  <h3 className="font-semibold text-lg mb-2">{content.title}</h3>
                )}

                <div
                  className="text-gray-700 mb-4 line-clamp-6 prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: renderMarkdown(content.text || '') }}
                />

                {content.hashtags && content.hashtags.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {content.hashtags.slice(0, 5).map((tag, index) => (
                      <span
                        key={index}
                        className="text-xs px-2 py-1 bg-primary-50 text-primary-600 rounded"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Schedule for Later: scheduled posts publish to FinCreator's house
              accounts, so, like publishing, it's admin only. */}
          {isAdmin && (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center gap-2 mb-4">
                <CalendarIcon className="h-6 w-6 text-primary-600" />
                <h2 className="text-xl font-bold text-gray-900">Schedule for Later</h2>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Pick a date and time to automatically publish these posts later, or publish them
                now using the buttons below.
              </p>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Scheduled Date &amp; Time
                  </label>
                  <input
                    type="datetime-local"
                    value={scheduledAt}
                    onChange={(e) => setScheduledAt(e.target.value)}
                    min={toDatetimeLocalString(new Date())}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <button
                  onClick={handleScheduleAll}
                  disabled={scheduling}
                  className="flex items-center justify-center gap-2 px-6 py-2 border border-primary-600 text-primary-600 font-semibold rounded-lg hover:bg-primary-50 disabled:opacity-50"
                >
                  {scheduling && <Spinner size="sm" />}
                  <ClockIcon className="h-5 w-5" />
                  {scheduling ? 'Scheduling...' : 'Schedule for Later'}
                </button>
              </div>
            </div>
          )}

          {/* Publish Buttons */}
          <div className="flex items-center justify-center gap-4">
            <button
              onClick={() => setStep(1)}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Create More Content
            </button>
            {isAdmin && (
              <button
                onClick={handlePublishAll}
                className="flex items-center gap-2 px-8 py-3 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700"
              >
                <RocketLaunchIcon className="h-5 w-5" />
                Publish to FinCreator's accounts
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

interface StepIndicatorProps {
  number: number
  label: string
  active: boolean
  completed: boolean
}

const StepIndicator = ({ number, label, active, completed }: StepIndicatorProps) => {
  return (
    <div className="flex flex-col items-center">
      <div
        className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg transition-all ${
          completed
            ? 'bg-green-500 text-white'
            : active
            ? 'bg-primary-600 text-white'
            : 'bg-gray-200 text-gray-500'
        }`}
      >
        {completed ? '✓' : number}
      </div>
      <div
        className={`mt-2 text-sm font-medium ${
          active ? 'text-gray-900' : 'text-gray-500'
        }`}
      >
        {label}
      </div>
    </div>
  )
}

export default CreateContent
